import { MediaItemCardInGroups } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardInGroups/MediaItemCardInGroups";
import { MediaItemCardDefault } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardDefault/MediaItemCardDefault";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaItemCardUnknown } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardUnknown/MediaItemCardUnknown";
import { MediaItemCardLocalCombination } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardLocalCombination/MediaItemCardLocalCombination";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { Media } from "@coworker/app/src/components/RoomSettings/types/media";
import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import {
  Article,
  EdsData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { isLocalCombinationArticle } from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { MediaItemCardLCSubItem } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardLocalCombination/MediaItemCardLCSubItem/MediaItemCardLCSubItem";
import {
  ItemType,
  UNKNOWN_ITEM_NAME,
} from "@coworker/apprestructured/src/shared/constants/itemType";
import LocalCombinationSubArticlePanel from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationSubArticlePanel";

interface ItemListViewItemCardProps<T extends Article | AggregatedArticle> {
  isItemInGroups: boolean;
  itemCardRenderType?: ItemCardRenderType;
  badges: ItemBadge[];
  mediaItemType?: MediaItemType;
  edsData?: EdsData | null;
  article: T;
  media: Media;
  selectArticleIdsMode?: boolean;
  mediaItemViewType?: MediaItemViewType;
}

export const ItemListViewItemCard = <T extends Article | AggregatedArticle>({
  isItemInGroups,
  itemCardRenderType = ItemCardRenderType.link,
  badges,
  edsData,
  article,
  media,
  selectArticleIdsMode = false,
  mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
  mediaItemType,
}: ItemListViewItemCardProps<T>) => {
  const { data: itemInfo, isLoading: isLoadingItemInfo } = useItemInfo(
    article.productArticleId
  );
  const enableLocalCombinations = useFlag(FLAGS.ENABLE_LOCAL_COMBINATIONS);
  const isLocalCombinationItem = isLocalCombinationArticle(
    article.productArticleType as ArticleType
  );
  const isUnknownItem =
    (article.productArticleType === ItemType.UNKNOWN ||
      article.productArticleType === ItemType.ARTICLE) &&
    article.itemName.toLowerCase() === UNKNOWN_ITEM_NAME;

  const isLcSubItem = mediaItemType === MediaItemType.LC_SUBITEM;

  return (
    <>
      {isLoadingItemInfo && <SimpleArticleCardSkeleton />}
      {!isLoadingItemInfo &&
        itemInfo &&
        (isLcSubItem ? (
          <>
            {article.parentArticle && (
              <LocalCombinationSubArticlePanel
                parentArticle={article.parentArticle}
                noBorderBottom={true}
              />
            )}
            <MediaItemCardLCSubItem
              mediaItemViewType={mediaItemViewType}
              itemInfo={itemInfo}
              article={article}
              edsData={edsData}
              totalQuantityPieces={
                (article.articlesCountInCombination ?? 1) * article.nbrArticles
              }
              itemCardRenderType={
                article.parentArticle
                  ? ItemCardRenderType.link
                  : ItemCardRenderType.static
              }
              shouldDisplayQuantity={true}
            />
          </>
        ) : isItemInGroups ? (
          <MediaItemCardInGroups<AggregatedArticle>
            itemCardRenderType={itemCardRenderType}
            badges={badges}
            edsData={edsData ?? null}
            item={itemInfo}
            article={article as AggregatedArticle}
            media={media}
            selectArticleIdsMode={selectArticleIdsMode}
            mediaItemViewType={
              mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
            }
          />
        ) : (
          <MediaItemCardDefault
            badges={badges}
            itemCardRenderType={itemCardRenderType}
            item={itemInfo}
            article={article}
            media={media}
            edsData={edsData ?? null}
            selectArticleIdsMode={selectArticleIdsMode}
            mediaItemViewType={
              mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
            }
          />
        ))}
      {((!itemInfo && !isLocalCombinationItem) ||
        (itemInfo && isUnknownItem)) && (
        <MediaItemCardUnknown
          itemCardRenderType={itemCardRenderType}
          badges={badges}
          itemNo={article.productArticleId}
          article={article}
          media={media}
          selectArticleIdsMode={selectArticleIdsMode}
          mediaItemViewType={
            mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
          }
        />
      )}
      {enableLocalCombinations && isLocalCombinationItem && (
        <MediaItemCardLocalCombination
          isItemInGroups={isItemInGroups}
          itemCardRenderType={itemCardRenderType}
          edsData={edsData ?? null}
          article={article}
          badges={badges}
          media={media}
          selectArticleIdsMode={selectArticleIdsMode}
          mediaItemViewType={
            mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
          }
        />
      )}
    </>
  );
};
